import React, { useRef, useState } from 'react';

import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ThreeLineHighlight, calculateTotalAnimationTime } from '@components/three-line-highlight';
import { breakpoints } from '@constants/breakpoints';
import { headingStyles } from '@components/typography/font-styles';
import { motion, useInView } from 'motion/react';

import { analytics } from '../../analytics';
import { Box, Container, Flex } from '../grid';
import { P } from '../typography/new';
import { NavLink } from '../nav-link';
import { SecondaryNavigation } from './secondary-navigation';
import { useNavigationItems } from '../../hooks/use-navigation-items';
import { Link } from '../link';
import { Icon } from '../icons';
import { LottieAnimator } from '../lottie-animator';
import decorativeBand from '../lottie-animator/lotties/Decorative-band.json';
import barciGrowBeat from '../lottie-animator/lotties/Barci-grow+beat.json';

const StarContainer = styled(Box)`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url('/static/images/footer/stars-bg.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.7;
  }

  > * {
    position: relative;
  }
`;

const shouldForwardProp = (prop, defaultValidatorFn) => {
  return !['transform', 'color', 'fontWeight'].includes(prop) && defaultValidatorFn(prop);
};

const AnimationTextStyle = styled.span.withConfig({
  shouldForwardProp,
})`
  ${headingStyles}
  font-size: 60px;
  line-height: 0.88em;
  letter-spacing: -0.01px;
  color: white;

  @media (min-width: ${breakpoints.lg}) {
    font-size: 158px;
    line-height: 140px;
    letter-spacing: -0.032px;
  }
`;

const StyledAnimationText = ({ ...rest }) => {
  const headingRef = useRef();

  return <AnimationTextStyle ref={headingRef} {...rest} />;
};

const Footer = ({ mt, hasAcknowledgement }) => {
  const navigationItems = useNavigationItems('secondary');
  const socialNavigationItems = useNavigationItems('social');
  const [shouldPlayChickenAnimation, setShouldPlayChickenAnimation] = useState(false);
  const animationRef = useRef(null);
  const isInView = useInView(animationRef, { amount: 0.1, once: true });

  const barciAnimationDelay =
    calculateTotalAnimationTime({
      delay: 0,
      headingCount: 3,
    }) - 0.2;

  const handleChickenAnimationTrigger = () => {
    // Add 1500ms delay before playing the animation
    const delayTime = barciAnimationDelay * 100 * 10;

    setTimeout(() => {
      setShouldPlayChickenAnimation(true);
    }, delayTime);
  };

  return (
    <Box mt={mt} backgroundColor="black">
      <StarContainer pt={{ _: hasAcknowledgement ? '72px' : '80px', lg: hasAcknowledgement ? '156px' : '80px' }}>
        <Container>
          <Flex flexDirection="column" alignItems="center">
            {hasAcknowledgement && (
              <>
                <P textAlign="center" color="white" variant="large" maxWidth="894px">
                  Nando’s acknowledges the Traditional Custodians of the land on which we live, work and serve our
                  customers and communities. We pay our respects to Elders past and present and to the rights they hold
                  as traditional custodians. We recognise the continuing connection to land, waters, culture and
                  community as the Traditional Custodians of Country throughout Australia.
                </P>
                <Flex flexDirection="column" alignItems="center" pt={{ _: '48px', lg: '80px' }}>
                  <motion.div
                    style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}
                    ref={animationRef}
                    onViewportEnter={handleChickenAnimationTrigger}
                  >
                    <ThreeLineHighlight
                      disableHighlightAnimation
                      fullTilt
                      headingLevel={StyledAnimationText}
                      lineNegativeMarginTop={{ xs: '-20px', lg: '-50px' }}
                      heading={['For the', 'love of', 'PERi-PERi']}
                      animate={isInView ? 'visible' : 'hidden'}
                    />
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                      transition={{
                        delay: barciAnimationDelay,
                      }}
                    >
                      <Flex
                        width={{ xs: '194px', lg: '365px' }}
                        mt={{ xs: '-45px', lg: '-110px' }}
                        mr={{ xs: '25px', lg: '115px' }}
                        position="relative"
                        zIndex="3"
                      >
                        <LottieAnimator animationDataFile={barciGrowBeat} shouldPlay={shouldPlayChickenAnimation} />
                      </Flex>
                    </motion.div>
                  </motion.div>
                </Flex>
              </>
            )}
            <Flex
              pt={{ xs: hasAcknowledgement && '24px', lg: '32px' }}
              mb={{ xs: '48px', lg: '60px' }}
              gap={{ xs: '24px', lg: '32px' }}
            >
              {socialNavigationItems.map(({ label, href, icon }) => (
                <Link
                  style={{ background: 'white' }}
                  href={href}
                  borderRadius="100%"
                  height={{ xs: '48px', lg: '64px' }}
                  width={{ xs: '48px', lg: '64px' }}
                  key={href}
                  target="_blank"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  title={label}
                >
                  <Icon fill height={{ xs: '24px', lg: '32px' }} width={{ xs: '24px', lg: '32px' }} name={icon} />
                </Link>
              ))}
            </Flex>
            <Flex
              gap={{ xs: '12px 16px', lg: '12px 32px' }}
              justifyContent="center"
              flexWrap="wrap"
              width="100%"
              height="100%"
              mb={{ xs: '40px', lg: '56px' }}
              maxWidth="1199px"
            >
              {navigationItems.map(({ label, href, target }) => (
                <NavLink
                  style={{ color: 'white' }}
                  onClick={() => {
                    analytics.track(ANALYTICS_EVENTS.FOOTER_NAV_ITEM_CLICKED, {
                      text: label,
                    });
                  }}
                  href={href}
                  key={href}
                  target={target}
                >
                  <P style={{ textTransform: 'uppercase' }} variant="standard-link">
                    {label}
                  </P>
                </NavLink>
              ))}
            </Flex>

            <SecondaryNavigation />
          </Flex>
        </Container>
        <LottieAnimator animationDataFile={decorativeBand} loop />
      </StarContainer>
    </Box>
  );
};

Footer.propTypes = {
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasAcknowledgement: PropTypes.bool,
};

Footer.defaultProps = {
  mt: 1,
  hasAcknowledgement: false,
};

export { Footer };
